import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { globalSearch } from "../lib/api";
import {
  House,
  VideoCamera,
  Kanban,
  FilmStrip,
  Users,
  Lightbulb,
  LightbulbFilament,
  CalendarBlank,
  ChartBar,
  DownloadSimple,
  UserCircle,
  Sparkle,
  MagnifyingGlass,
  SignOut,
  List,
  X,
  CaretLeft,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

const navItems = [
  { path: "/", icon: House, label: "Dashboard", roles: ["owner", "admin", "manager", "editor", "viewer"] },
  { path: "/channels", icon: VideoCamera, label: "Channels", roles: ["owner", "admin", "manager", "editor", "viewer"] },
  { path: "/pipeline", icon: Kanban, label: "Pipeline", roles: ["owner", "admin", "manager", "editor", "viewer"] },
  { path: "/shorts", icon: FilmStrip, label: "Shorts", roles: ["owner", "admin", "manager", "viewer"] },
  { path: "/editors", icon: Users, label: "Editors", roles: ["owner", "admin", "viewer"] },
  { path: "/concepts", icon: Lightbulb, label: "Concepts", roles: ["owner", "admin", "viewer"] },
  { path: "/ideas", icon: LightbulbFilament, label: "Ideas Bank", roles: ["owner", "admin", "manager", "viewer"] },
  { path: "/calendar", icon: CalendarBlank, label: "Calendar", roles: ["owner", "admin", "manager", "editor", "viewer"] },
  { path: "/reports", icon: ChartBar, label: "Reports", roles: ["owner", "admin", "manager", "viewer"] },
  { path: "/import-export", icon: DownloadSimple, label: "Import/Export", roles: ["owner", "admin", "manager", "viewer"] },
  { path: "/users", icon: UserCircle, label: "Users", roles: ["owner", "admin", "manager"] },
  { path: "/editor-report", icon: ChartBar, label: "Editor Report", roles: ["owner", "admin"] },
  { path: "/ai-insights", icon: Sparkle, label: "AI Insights", roles: ["owner", "admin", "manager", "viewer"] },
];

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const filteredNavItems = navItems.filter((item) =>
    item.roles.includes(user?.role || "viewer")
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const search = async () => {
      if (searchQuery.length < 2) {
        setSearchResults(null);
        return;
      }
      setSearching(true);
      try {
        const response = await globalSearch(searchQuery);
        setSearchResults(response.data);
      } catch (err) {
        // Search error silently handled
      } finally {
        setSearching(false);
      }
    };
    const timeout = setTimeout(search, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSearchSelect = (type, item) => {
    setSearchOpen(false);
    setSearchQuery("");
    if (type === "videos") {
      navigate(`/pipeline?video=${item.id}`);
    } else if (type === "channels") {
      navigate(`/channels/${item.id}`);
    } else if (type === "ideas") {
      navigate(`/ideas?idea=${item.id}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col h-screen bg-[#0B0C10] border-r border-white/5 transition-all duration-300 ${
          collapsed ? "w-16" : "w-64"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <VideoCamera size={18} weight="bold" className="text-white" />
              </div>
              <span className="font-heading font-semibold text-white">PIXELPURE</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
            data-testid="sidebar-collapse-btn"
          >
            <CaretLeft
              size={18}
              className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
            />
          </button>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-gray-400"
            data-testid="sidebar-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 scrollbar-thin">
          <ul className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? "bg-blue-600/20 text-blue-400 border-l-2 border-blue-500"
                        : "text-[#A0AEC0] hover:text-white hover:bg-white/5"
                    } ${collapsed ? "justify-center" : ""}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User menu */}
        <div className="p-4 border-t border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-3 w-full rounded-lg p-2 hover:bg-white/5 transition-colors ${
                  collapsed ? "justify-center" : ""
                }`}
                data-testid="user-menu-btn"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="bg-blue-600 text-white text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-white truncate">
                      {user?.name || "User"}
                    </p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#1A1A2E] border-white/10">
              <DropdownMenuItem className="text-gray-300" disabled>
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                data-testid="logout-btn"
              >
                <SignOut size={16} className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 sticky top-0 z-40 backdrop-blur-2xl bg-[#1A1A2E]/70 border-b border-white/5 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-gray-400"
              data-testid="mobile-menu-btn"
            >
              <List size={24} />
            </button>
            <h1 className="font-heading text-lg font-medium text-white hidden sm:block">
              {filteredNavItems.find((item) => item.path === location.pathname)?.label ||
                "Dashboard"}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              data-testid="global-search-btn"
            >
              <MagnifyingGlass size={18} />
              <span className="hidden sm:inline text-sm">Search...</span>
              <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-xs">
                <span>⌘</span>K
              </kbd>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="px-4 lg:px-6 py-3 border-t border-white/5 text-center">
          <p className="text-xs text-gray-600">
            Aipera Technology All Rights Reserved &middot; contact@aipera.com
          </p>
        </footer>
      </div>

      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 p-0 max-w-lg">
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <MagnifyingGlass size={20} className="text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search videos, channels, ideas..."
                className="flex-1 bg-transparent border-0 focus-visible:ring-0 text-white placeholder:text-gray-500"
                data-testid="search-input"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto p-2">
            {searching && (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!searching && searchResults && (
              <div className="space-y-4">
                {searchResults.videos?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-gray-500 px-2 mb-2">
                      Videos
                    </h3>
                    {searchResults.videos.map((video) => (
                      <button
                        key={video.id}
                        onClick={() => handleSearchSelect("videos", video)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        data-testid={`search-result-video-${video.id}`}
                      >
                        <VideoCamera size={16} className="text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{video.title}</p>
                          <p className="text-xs text-gray-500">{video.stage}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.channels?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-gray-500 px-2 mb-2">
                      Channels
                    </h3>
                    {searchResults.channels.map((channel) => (
                      <button
                        key={channel.id}
                        onClick={() => handleSearchSelect("channels", channel)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        data-testid={`search-result-channel-${channel.id}`}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: channel.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{channel.name}</p>
                          <p className="text-xs text-gray-500">{channel.niche}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.ideas?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-gray-500 px-2 mb-2">
                      Ideas
                    </h3>
                    {searchResults.ideas.map((idea) => (
                      <button
                        key={idea.id}
                        onClick={() => handleSearchSelect("ideas", idea)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
                        data-testid={`search-result-idea-${idea.id}`}
                      >
                        <Lightbulb size={16} className="text-gray-400" />
                        <p className="text-sm text-white truncate">{idea.title}</p>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.videos?.length === 0 &&
                  searchResults.channels?.length === 0 &&
                  searchResults.ideas?.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No results found</p>
                  )}
              </div>
            )}
            {!searching && !searchResults && (
              <p className="text-center text-gray-500 py-8">
                Start typing to search...
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
